import type {
  AlarmConfig,
  InterlockConfig,
  LoopConfig,
  SequenceConfig,
} from '../../engine/plant/types';

export const loops: LoopConfig[] = [
  {
    id: 'FIC-101',
    name: 'Feed A flow',
    description: 'Sets the unit rate. Feed B follows it on ratio.',
    guidance:
      'This is the wild flow: everything else on the unit is sized against it. Changing it ' +
      'changes the residence time, the heat release and the jacket duty all at once, so move ' +
      'it slowly and let the temperature loop catch up.',
    pv: 'FT-101.PV',
    out: 'FV-101.CMD',
    sp: 8,
    mode: 'AUTO',
    badPv: 'shedToMan',
    tuning: { kc: 0.5, ti: 12, td: 0, direction: 'reverse', spLo: 0, spHi: 14, opLo: 0, opHi: 100 },
  },
  {
    id: 'FFIC-102',
    name: 'Feed B ratio',
    description: 'Holds feed B at a fixed ratio to feed A.',
    guidance:
      'Ratio control: the setpoint is 0.50 × the measured feed A flow. Raising the ratio ' +
      'dilutes the reactor — less heat release, lower conversion. It is a legitimate way to ' +
      'take the edge off a temperature excursion when the jacket is already flat out.',
    pv: 'FT-102.PV',
    out: 'FV-102.CMD',
    sp: 4,
    mode: 'AUTO',
    badPv: 'shedToMan',
    ratio: { wild: 'FT-101.PV', initial: 0.5, lo: 0.2, hi: 1.2 },
    tuning: { kc: 0.5, ti: 12, td: 0, direction: 'reverse', spLo: 0, spHi: 10, opLo: 0, opHi: 100 },
  },
  {
    id: 'TIC-103',
    name: 'Feed preheat temperature',
    description: 'Hot-oil valve on the feed preheater.',
    guidance:
      'A colder feed absorbs more of the reaction heat, so it looks like extra cooling — but ' +
      'it also slows the reaction and drops conversion. Use the jacket for temperature control ' +
      'and leave the preheat where it is.',
    pv: 'TT-103.PV',
    out: 'FV-103.CMD',
    sp: 310.15,
    mode: 'AUTO',
    badPv: 'holdOp',
    tuning: { kc: 2.5, ti: 120, td: 0, direction: 'reverse', spLo: 295, spHi: 330, opLo: 0, opHi: 100 },
  },
  {
    id: 'TIC-201',
    name: 'Reactor temperature',
    description: 'Cascade primary — its output is the jacket cooling-water flow setpoint.',
    guidance:
      'DIRECT acting: a rising reactor temperature calls for more cooling water. The reaction ' +
      'rate doubles roughly every 8 K, so the loop has to act before the temperature has moved ' +
      'far. If FIC-202 leaves CAS this loop tracks its setpoint, so re-engaging is bumpless. ' +
      'If the jacket saturates, your remaining levers are feed rate and feed ratio.',
    pv: 'TT-201.PV',
    out: 'TIC-201.OP',
    sp: 350,
    mode: 'AUTO',
    cascade: { secondary: 'FIC-202' },
    badPv: 'shedToMan',
    // Derivative earns its place on a two-lag thermal process, but the filter divisor is
    // 6 rather than the usual 10: at Kc = 6 the high-frequency gain would otherwise be 60,
    // and the thermocouple noise alone would chatter the cooling-water valve.
    tuning: { kc: 6, ti: 300, td: 25, dFilterN: 6, direction: 'direct', spLo: 320, spHi: 375, opLo: 0, opHi: 100 },
  },
  {
    id: 'FIC-202',
    name: 'Jacket cooling-water flow',
    description: 'Cascade secondary under the reactor temperature controller.',
    guidance:
      'A fast inner loop. Its job is to make the jacket flow follow the temperature ' +
      'controller quickly enough that the outer loop sees a clean, linear actuator.',
    pv: 'FT-202.PV',
    out: 'FV-202.CMD',
    sp: 15.7,
    mode: 'CAS',
    badPv: 'shedToMan',
    tuning: { kc: 0.5, ti: 12, td: 0, direction: 'reverse', spLo: 0, spHi: 40, opLo: 0, opHi: 100 },
  },
  {
    id: 'LIC-203',
    name: 'Reactor level',
    description: 'Reactor outlet valve holds the liquid inventory.',
    guidance:
      'The level sets the residence time and the heat-transfer area. Losing level loses both, ' +
      'and a low level with the agitator running risks vortexing. Keep it near 80 %.',
    pv: 'LT-203.PV',
    out: 'FV-203.CMD',
    sp: 80,
    mode: 'AUTO',
    badPv: 'shedToMan',
    tuning: { kc: 3, ti: 400, td: 0, direction: 'direct', spLo: 30, spHi: 95, opLo: 0, opHi: 100 },
  },
  {
    id: 'PIC-301',
    name: 'Separator pressure',
    description: 'Vent valve to flare.',
    guidance:
      'The light ends flashing off the product set the pressure. It rises with reactor ' +
      'temperature, so a pressure excursion here is often a temperature problem upstream.',
    pv: 'PT-301.PV',
    out: 'FV-301.CMD',
    sp: 2.0,
    mode: 'AUTO',
    badPv: 'shedToMan',
    tuning: { kc: 3, ti: 40, td: 0, direction: 'direct', spLo: 1.2, spHi: 4, opLo: 0, opHi: 100 },
  },
  {
    id: 'LIC-302',
    name: 'Separator level',
    description: 'Product valve to the tank.',
    guidance:
      'Losing this level breaks the liquid seal and vents the separator to the product tank. ' +
      'Keep it above 20 %.',
    pv: 'LT-302.PV',
    out: 'FV-302.CMD',
    sp: 50,
    mode: 'AUTO',
    badPv: 'shedToMan',
    tuning: { kc: 3, ti: 300, td: 0, direction: 'direct', spLo: 15, spHi: 85, opLo: 0, opHi: 100 },
  },
];

export const sequences: SequenceConfig[] = [
  {
    id: 'SHUTDOWN',
    name: 'Controlled shutdown',
    description:
      'Cuts the feed, keeps the cooling on until the residual reaction heat is gone, then ' +
      'stops the agitator. Roughly twenty minutes.',
    permissive: { op: 'true' },
    steps: [
      {
        id: 'CUT-FEED',
        name: 'Cut feed',
        prompt: 'Closing both feed valves and holding the jacket at full flow.',
        onEntry: [
          // Emptying the reactor takes the level through the low-low trip, which exists
          // to stop the vessel running dry with the agitator turning. A planned shutdown
          // defeats it under permit — logged, and removed again when the sequence ends.
          { do: 'bypass', id: 'I-203', on: true, reason: 'Controlled shutdown — reactor is being emptied deliberately' },
          { do: 'setMode', loop: 'FIC-101', mode: 'MAN' },
          { do: 'setOp', loop: 'FIC-101', value: 0 },
          { do: 'setMode', loop: 'FFIC-102', mode: 'MAN' },
          { do: 'setOp', loop: 'FFIC-102', value: 0 },
          { do: 'setMode', loop: 'FIC-202', mode: 'MAN' },
          { do: 'setOp', loop: 'FIC-202', value: 100 },
        ],
        holdS: 120,
      },
      {
        id: 'COOL',
        name: 'Cool down',
        prompt: 'Holding full cooling until the reactor is below 320 K.',
        holdS: 300,
        until: { op: '<', tag: 'TT-201.PV', value: 320 },
        timeoutS: 3600,
      },
      {
        id: 'DRAIN',
        name: 'Empty the reactor',
        prompt: 'Draining the reactor to the separator.',
        onEntry: [
          { do: 'setMode', loop: 'LIC-203', mode: 'MAN' },
          { do: 'setOp', loop: 'LIC-203', value: 100 },
        ],
        holdS: 120,
        until: { op: '<', tag: 'LT-203.PV', value: 12 },
        timeoutS: 1800,
      },
      {
        id: 'STOP-AGITATOR',
        name: 'Stop the agitator',
        prompt: 'Unit shut down.',
        onEntry: [
          { do: 'setTag', tag: 'AG-201.RUN', value: 0 },
          { do: 'setMode', loop: 'LIC-203', mode: 'MAN' },
          { do: 'setOp', loop: 'LIC-203', value: 0 },
        ],
        holdS: 5,
      },
    ],
    onComplete: [{ do: 'bypass', id: 'I-203', on: false, reason: '' }],
    onAbort: [{ do: 'bypass', id: 'I-203', on: false, reason: '' }],
  },
];

const g = (cause: string, consequence: string, action: string): Pick<AlarmConfig, 'cause' | 'consequence' | 'action'> => ({ cause, consequence, action });

export const alarms: AlarmConfig[] = [
  {
    id: 'TT-201.H',
    tag: 'TT-201.PV',
    kind: 'H',
    limit: 358,
    deadband: 1.5,
    onDelay: 10,
    priority: 'high',
    text: 'Reactor temperature high',
    ...g(
      'Cooling lost or reduced, feed concentration up, agitator degraded, or the temperature controller in manual.',
      'The reaction rate roughly doubles every 8 K. Left alone this becomes a runaway.',
      'Check FIC-202 is in CAS and the jacket flow is responding. If the jacket is already at maximum, cut the feed rate or raise the feed ratio to dilute.',
    ),
  },
  {
    id: 'TT-201.HH',
    tag: 'TT-201.PV',
    kind: 'HH',
    limit: 380,
    deadband: 2,
    onDelay: 2,
    priority: 'emergency',
    text: 'Reactor temperature very high — trip',
    ...g(
      'A runaway in progress.',
      'The feed cut and the emergency quench will fire. Above about 400 K the vessel relief lifts.',
      'Let the trip act. Do not try to catch it manually. Once the temperature is falling, find out why the cooling was lost before you reset.',
    ),
  },
  {
    id: 'TT-201.ROC',
    tag: 'TT-201.PV',
    kind: 'rocHi',
    limit: 0.02,
    rocOverS: 60,
    onDelay: 5,
    priority: 'high',
    text: 'Reactor temperature rising fast',
    ...g(
      'Heat generation is outrunning heat removal.',
      'A rate of change alarm gives you a few minutes of warning that the absolute-value alarm will not.',
      'Act now, not when the high alarm comes in: more cooling, less feed, or both.',
    ),
  },
  {
    id: 'TT-201.L',
    tag: 'TT-201.PV',
    kind: 'L',
    limit: 335,
    deadband: 1.5,
    onDelay: 60,
    priority: 'medium',
    text: 'Reactor temperature low',
    ...g('Over-cooling, cold feed, or the reaction has stopped.', 'Conversion collapses and unreacted A carries through to the product tank.', 'Check the preheater and the jacket flow. Below about 330 K the reaction is too slow to be worth running.'),
  },
  {
    id: 'FT-202.L',
    tag: 'FT-202.PV',
    kind: 'L',
    limit: 4,
    deadband: 1,
    onDelay: 10,
    priority: 'high',
    text: 'Jacket cooling-water flow low',
    ...g('Cooling-water pump tripped, valve stuck shut, or the header lost pressure.', 'The reactor has no heat sink. Temperature will rise within minutes.', 'Confirm the cooling-water pump is running. If it is not, cut the feed immediately — do not wait for the temperature alarm.'),
  },
  {
    id: 'AG-201.STOP',
    tag: 'AG-201.RUN',
    kind: 'digital',
    digitalState: 0,
    onDelay: 5,
    priority: 'high',
    text: 'Agitator stopped',
    ...g(
      'Motor trip, or stopped by the operator.',
      'Without agitation the jacket heat-transfer coefficient falls to about a third. The reactor loses two-thirds of its cooling capacity over the next few minutes.',
      'Restart the agitator. If it will not restart, cut the feed and let the reactor cool.',
    ),
  },
  {
    id: 'PT-301.H',
    tag: 'PT-301.PV',
    kind: 'H',
    limit: 2.8,
    deadband: 0.1,
    onDelay: 10,
    priority: 'high',
    text: 'Separator pressure high',
    ...g('More light ends flashing than the vent can pass — usually because the reactor is hot.', 'The relief valve lifts at 3.2 bara and vents product to flare.', 'Check PIC-301 is in AUTO with the vent valve responding, then look upstream at the reactor temperature.'),
  },
  {
    id: 'PT-301.HH',
    tag: 'PT-301.PV',
    kind: 'HH',
    limit: 3.2,
    deadband: 0.1,
    onDelay: 2,
    priority: 'emergency',
    text: 'Separator pressure very high',
    ...g('Vent path blocked or grossly undersized for the current vapour load.', 'The relief lifts. Product goes to flare and the unit is out of containment.', 'Open the vent fully, then cut the feed.'),
  },
  {
    id: 'LT-203.L',
    tag: 'LT-203.PV',
    kind: 'L',
    limit: 45,
    deadband: 2,
    onDelay: 30,
    priority: 'medium',
    text: 'Reactor level low',
    ...g('Outlet valve too far open, or the feed has fallen away.', 'Less liquid means less heat-transfer area and a shorter residence time — the reactor gets harder to control just as conversion drops.', 'Check LIC-203 and the feed flows.'),
  },
  {
    id: 'LT-203.LL',
    tag: 'LT-203.PV',
    kind: 'LL',
    limit: 25,
    deadband: 2,
    onDelay: 10,
    priority: 'emergency',
    text: 'Reactor level very low',
    ...g('Sustained loss of inventory.', 'The agitator will vortex and the jacket loses wetted area — the two things keeping the exotherm under control.', 'The trip will cut the feed and stop the agitator. Restore level before restarting.'),
  },
  {
    id: 'LT-302.L',
    tag: 'LT-302.PV',
    kind: 'L',
    limit: 20,
    deadband: 2,
    onDelay: 20,
    priority: 'medium',
    text: 'Separator level low',
    ...g('Product valve too far open.', 'Losing the liquid seal blows separator vapour into the product tank.', 'Take LIC-302 to manual and close the valve until the level recovers.'),
  },
  {
    id: 'AT-201.H',
    tag: 'AT-201.PV',
    kind: 'H',
    limit: 0.25,
    deadband: 0.03,
    onDelay: 120,
    priority: 'medium',
    text: 'Unreacted A high — conversion off spec',
    ...g('Reactor too cold, residence time too short, or feed A concentration up.', 'Product is off spec and unreacted A carries into the product tank.', 'Raise the reactor temperature setpoint, or reduce the feed rate to lengthen the residence time.'),
  },
];

export const interlocks: InterlockConfig[] = [
  {
    id: 'I-201',
    name: 'Reactor high-high temperature — feed cut and emergency quench',
    description:
      'At 380 K the unit cuts both feeds, drives the jacket to full flow and opens the ' +
      'emergency quench. Latching: reset only once the reactor is back below 355 K.',
    cause: { op: '>', tag: 'TT-201.PV', value: 380 },
    effects: [
      { do: 'setMode', loop: 'FIC-101', mode: 'MAN' },
      { do: 'setOp', loop: 'FIC-101', value: 0 },
      { do: 'setMode', loop: 'FFIC-102', mode: 'MAN' },
      { do: 'setOp', loop: 'FFIC-102', value: 0 },
      { do: 'setMode', loop: 'FIC-202', mode: 'MAN' },
      { do: 'setOp', loop: 'FIC-202', value: 100 },
      { do: 'setTag', tag: 'FV-901.CMD', value: 100 },
    ],
    latching: true,
    resetPermissive: { op: '<', tag: 'TT-201.PV', value: 355 },
    priority: 'emergency',
    bypassable: false,
    firstOut: [
      { id: 'T-HH', text: 'Reactor temperature high-high', cond: { op: '>', tag: 'TT-201.PV', value: 380 } },
      { id: 'CW-LOW', text: 'Jacket cooling-water flow low', cond: { op: '<', tag: 'FT-202.PV', value: 4 } },
      { id: 'AGITATOR', text: 'Agitator stopped', cond: { op: '<', tag: 'AG-201.RUN', value: 0.5 } },
      { id: 'T-ROC', text: 'Reactor temperature rate of change high', cond: { op: 'rate', tag: 'TT-201.PV', overS: 60, gt: 0.02 } },
    ],
    onReset: [{ do: 'setTag', tag: 'FV-901.CMD', value: 0 }],
  },
  {
    id: 'I-203',
    name: 'Reactor low-low level — feed cut and agitator stop',
    description:
      'Protects the agitator and the jacket wetted area. Reset once the level is back above 45 %.',
    cause: { op: 'for', term: { op: '<', tag: 'LT-203.PV', value: 25 }, seconds: 10 },
    effects: [
      { do: 'setMode', loop: 'FIC-101', mode: 'MAN' },
      { do: 'setOp', loop: 'FIC-101', value: 0 },
      { do: 'setMode', loop: 'FFIC-102', mode: 'MAN' },
      { do: 'setOp', loop: 'FFIC-102', value: 0 },
      { do: 'setTag', tag: 'AG-201.RUN', value: 0 },
      { do: 'setMode', loop: 'LIC-203', mode: 'MAN' },
      { do: 'setOp', loop: 'LIC-203', value: 0 },
    ],
    latching: true,
    resetPermissive: { op: '>', tag: 'LT-203.PV', value: 45 },
    priority: 'emergency',
    bypassable: true,
    firstOut: [{ id: 'L-LL', text: 'Reactor level low-low', cond: { op: '<', tag: 'LT-203.PV', value: 25 } }],
    onReset: [{ do: 'setTag', tag: 'AG-201.RUN', value: 1 }],
  },
  {
    id: 'I-301',
    name: 'Separator high-high pressure — vent wide open and feed cut',
    description: 'At 3.2 bara the vent goes fully open and the feed is cut. Reset below 2.4 bara.',
    cause: { op: '>', tag: 'PT-301.PV', value: 3.2 },
    effects: [
      { do: 'setMode', loop: 'PIC-301', mode: 'MAN' },
      { do: 'setOp', loop: 'PIC-301', value: 100 },
      { do: 'setMode', loop: 'FIC-101', mode: 'MAN' },
      { do: 'setOp', loop: 'FIC-101', value: 0 },
      { do: 'setMode', loop: 'FFIC-102', mode: 'MAN' },
      { do: 'setOp', loop: 'FFIC-102', value: 0 },
    ],
    latching: true,
    resetPermissive: { op: '<', tag: 'PT-301.PV', value: 2.4 },
    priority: 'emergency',
    bypassable: true,
    firstOut: [
      { id: 'P-HH', text: 'Separator pressure high-high', cond: { op: '>', tag: 'PT-301.PV', value: 3.2 } },
      { id: 'T-HIGH', text: 'Reactor temperature high', cond: { op: '>', tag: 'TT-201.PV', value: 358 } },
    ],
  },
];
